import {createRouter, createWebHistory} from 'vue-router'
import {ElMessage} from 'element-plus'

// 布局组件
import MainLayout from '@/layouts/MainLayout.vue'
import EmptyLayout from '@/layouts/EmptyLayout.vue'

// 页面组件
import Login from '@/views/auth/Login.vue'
import WorkOrderList from '@/views/workorder/List.vue'
import Dashboard from '@/views/dashboard/Index.vue'
import WorkOrderDetail from '@/views/WorkOrderDetail.vue'
import Profile from '@/views/user/Profile.vue'
import NotFound from '@/views/error/NotFound.vue'
import Cookies from "js-cookie";
// import {useAuthStore} from "@/stores/auth";


const routes = [
    {
        path: '/',
        redirect: '/dashboard',
        meta: {requiresAuth: true}
    },

    // 认证相关路由
    {
        path: '/auth',
        component: EmptyLayout,
        meta: {requiresAuth: false},
        children: [
            {
                path: 'login',
                name: 'Login',
                component: Login,
                meta: {title: '登录'}
            },
            {
                path: 'forgot-password',
                name: 'ForgotPassword',
                component: () => import('@/views/auth/ForgotPassword.vue'),
                meta: {title: '找回密码'}
            }
        ]
    },

    // 主应用路由
    {
        path: '/',
        component: MainLayout,
        meta: {requiresAuth: true},
        children: [
            // 仪表板
            {
                path: 'dashboard',
                name: 'Dashboard',
                component: Dashboard,
                meta: {
                    title: '仪表板',
                    icon: 'Odometer',
                    breadcrumb: ['仪表板']
                }
            },
            {
                path: '/workorders/create',
                name: 'CreateWorkOrder',
                component: () => import('@/views/workorder/Create.vue'),
                meta: {title: '创建工单', requiresAuth: true}
            },

            // 工单列表
            {
                path: 'workorders',
                name: 'WorkOrderList',
                component: WorkOrderList,
                meta: {
                    title: '工单管理',
                    icon: 'Document',
                    breadcrumb: ['工单管理'],
                    requiresPermission: ['workorder.view']
                }
            },

            // 工单详情
            {
                path: 'workorders/:id',
                name: 'WorkOrderDetail',
                component: WorkOrderDetail,
                meta: {
                    title: '工单详情',
                    breadcrumb: ['工单管理', '工单详情'],
                    hideInMenu: true
                }
            },

            // 用户管理模块 - 使用新的布局
            {
                path: 'users',
                name: 'UserManagement',
                component: () => import('@/views/user/UserManagementLayout.vue'),
                meta: {
                    title: '用户管理',
                    icon: 'User',
                    breadcrumb: ['用户管理'],
                    requiresAuth: true
                },
                children: [
                    // 用户列表
                    {
                        path: '',
                        name: 'UserList',
                        component: () => import('@/views/user/List.vue'),
                        meta: {
                            title: '用户列表',
                            breadcrumb: ['用户管理', '用户列表']
                        }
                    },
                    // 部署环境配置
                    {
                        path: 'deployment',
                        name: 'DeploymentConfig',
                        component: () => import('@/views/user/DeploymentConfig.vue'),
                        meta: {
                            title: '部署环境配置',
                            breadcrumb: ['用户管理', '部署环境配置'],
                            requiresSuperAdmin: true
                        }
                    },
                    // 用户组管理
                    {
                        path: 'user-groups',
                        name: 'UserGroup',
                        component: () => import('@/views/user/UserGroup.vue'),
                        meta: {
                            title: '用户组管理',
                            breadcrumb: ['用户管理', '用户组管理'],
                            requiresSuperAdmin: true
                        }
                    }
                ]
            },

            // 个人中心
            {
                path: '/profile',
                name: 'Profile',
                component: Profile,
                meta: {
                    title: '个人资料',
                    icon: 'User',
                    breadcrumb: ['个人资料']
                }
            },

            // 错误页面
            {
                path: '404',
                name: 'NotFound',
                component: NotFound,
                meta: {title: '页面不存在'}
            }
        ]
    },

    // 404页面
    {
        path: '/:pathMatch(.*)*',
        redirect: '/404'
    }
]

const router = createRouter({
    history: createWebHistory('/'),
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition
        } else {
            return {top: 0}
        }
    }
})

// 路由守卫 - 增强版
router.beforeEach(async (to, from, next) => {
    // 设置页面标题
    document.title = to.meta.title ? `${to.meta.title} - 工单管理系统` : '工单管理系统'

    // 检查是否需要登录
    if (to.meta.requiresAuth) {
        const token = Cookies.get('access_token')
        if (!token) {
            next('/auth/login')
            return
        }
    }

    // 检查超级管理员权限的新方法：通过 token 验证
    if (to.meta.requiresSuperAdmin) {
        try {
            const token = Cookies.get('access_token')
            if (!token) {
                next('/auth/login')
                return
            }

            // 从 token 或 localStorage 中获取用户信息
            const userInfo = Cookies.get('user_info') || localStorage.getItem('user_info')
            let user = null
            if (userInfo) {
                try {
                    user = JSON.parse(userInfo)
                } catch (e) {
                    console.error('解析用户信息失败:', e)
                }
            }

            // 检查是否是超级管理员或 admin
            const isSuperAdmin = user?.role === 'super_admin' || user?.is_super_admin || user?.role === 'admin'

            if (!isSuperAdmin) {
                ElMessage.warning('您没有访问此页面的权限')
                next('/dashboard')
                return
            }
        } catch (error) {
            console.error('权限检查失败:', error)
            ElMessage.error('权限检查失败')
            next('/dashboard')
            return
        }
    }

    // // 如果需要超级管理员权限，检查用户权限
    // if (to.meta.requiresSuperAdmin) {
    //     try {
    //         // 使用 authStore
    //         const authStore = useAuthStore()
    //         const user = authStore.user
    //
    //         // 检查是否是超级管理员或 admin
    //         const isSuperAdmin = user?.role === 'super_admin' || user?.is_super_admin || user?.role === 'admin'
    //
    //         if (!isSuperAdmin) {
    //             ElMessage.warning('您没有访问此页面的权限')
    //             next('/dashboard')
    //             return
    //         }
    //     } catch (error) {
    //         console.error('权限检查失败:', error)
    //         ElMessage.error('权限检查失败')
    //         next('/dashboard')
    //         return
    //     }
    // }

    next()
})
// router.beforeEach(async (to, from, next) => {
//     // 设置页面标题
//     document.title = to.meta.title ? `${to.meta.title} - 工单管理系统` : '工单管理系统'
//
//     // 检查是否需要登录
//     if (to.meta.requiresAuth) {
//         const token = Cookies.get('access_token')
//         if (!token) {
//             next('/login')
//             return
//         }
//     }
//
//     // 特定页面的权限检查
//     if (to.name === 'UserList' || to.name === 'CreateWorkOrder') {
//         try {
//             const permissionResponse = await checkPermission(
//                 'production',
//                 'v1.0'
//             )
//
//             if (!permissionResponse.has_permission) {
//                 if (permissionResponse.user_role !== 'admin' && to.name === 'UserList') {
//                     ElMessage.warning('您没有用户管理权限')
//                     next('/dashboard')
//                     return
//                 }
//
//                 if (!permissionResponse.has_permission && to.name === 'CreateWorkOrder') {
//                     ElMessage.warning('您没有创建工单的权限')
//                     next('/dashboard')
//                     return
//                 }
//             }
//         } catch (error) {
//             console.error('权限检查失败:', error)
//             ElMessage.error('权限检查失败')
//             next('/dashboard')
//             return
//         }
//     }
//
//     // 检查超级管理员权限
//     if (to.meta.requiresSuperAdmin) {
//         const authStore = useAuthStore()
//         const isSuperAdmin = authStore.user?.role === 'super_admin' || authStore.user?.is_super_admin
//
//         if (!isSuperAdmin) {
//             ElMessage.warning('您没有访问此页面的权限')
//             next('/dashboard')
//             return
//         }
//     }
//
//     next()
// })

// 路由错误处理
router.onError((error) => {
    console.error('路由错误:', error)

    if (error.message.includes('Failed to fetch dynamically imported module')) {
        ElMessage.error('组件加载失败，请刷新页面')
    }
})

export default router